import {useLocation} from '@tanstack/react-router';
import {useMemo} from 'react';

import {
  evaluationPipelineIdQueryParam,
  returnPathQueryParam,
  Route,
  trainingPipelineIdQueryParam,
  useQueryParam,
  viewPipelineIdQueryParam,
} from '~utils/routeUtil';
import {AnnotationSurface, AnnotationTelemetryPayload} from '~redux/types/images';

import {AnnotationUIContext, useAnnotationUIContext} from './useAnnotationUIContext';

/**
 * The UI context is a route-derived label; the surface is the value the backend stores. They are
 * kept as separate vocabularies so the telemetry enum can stay stable while routes move around.
 */
const SURFACE_BY_UI_CONTEXT: Record<AnnotationUIContext | 'unknown', AnnotationSurface> = {
  database: 'database',
  'model-samples': 'model_samples',
  'confusion-matrix': 'confusion_matrix',
  'model-test': 'model_test',
  'similarity-search': 'similarity_search',
  'improve-dataset': 'improve_dataset',
  'model-finetune': 'model_finetune',
  unknown: 'unknown',
};

/**
 * Surfaces the UI context does not distinguish: it reports smart select as model samples, add samples
 * (a model samples page) as unknown, and an image opened from the improve-dataset or smart-select
 * results as the plain finetune / samples image view. Those image views are told apart by the results
 * page they return to. Resolved from
 * the route instead of widening the UI context, which is persisted on the annotation itself and must
 * keep its existing values.
 */
function getSurfaceFromPathname(pathname: string, returnPath: string | undefined): AnnotationSurface | undefined {
  if (pathname.startsWith(Route.modelSamplesSmartSelect)) {
    return 'smart_select';
  }
  if (pathname.startsWith(Route.modelAddSamples)) {
    return 'model_samples';
  }
  if (pathname === Route.modelFinetuneImage && returnPath === Route.modelFinetuneImproveDataset) {
    return 'improve_dataset';
  }
  if (pathname === Route.modelSamplesImage && returnPath === Route.modelSamplesSmartSelectResults) {
    return 'smart_select';
  }
  return undefined;
}

/**
 * Telemetry context sent with every annotation save: the surface the user annotated from and the id
 * of the run that produced the images being shown.
 *
 * The identifier is always a pipeline id — never a tag, a confusion matrix cell or a search
 * descriptor — so every telemetry record holds the same kind of id. Surfaces that no pipeline
 * prepares (archive/database, similarity search) send none.
 */
export function useAnnotationTelemetry(): AnnotationTelemetryPayload {
  const uiContext = useAnnotationUIContext();
  const pathname = useLocation().pathname;
  const [returnPathValue] = useQueryParam(returnPathQueryParam);
  const returnPath = typeof returnPathValue === 'string' ? returnPathValue : undefined;
  const [viewPipelineId] = useQueryParam(viewPipelineIdQueryParam);
  const [evaluationPipelineId] = useQueryParam(evaluationPipelineIdQueryParam);
  const [trainingPipelineId] = useQueryParam(trainingPipelineIdQueryParam);

  return useMemo(() => {
    const surface = getSurfaceFromPathname(pathname, returnPath) ?? SURFACE_BY_UI_CONTEXT[uiContext];

    // Curation runs and the pipeline behind a confusion matrix cell are carried in viewPipelineId by
    // the navigation that opened the view; test and finetune views carry their own pipeline. Every
    // navigation that sets viewPipelineId leads to one of the first three surfaces, so on a test or
    // finetune view it can only be a leftover id of another kind of pipeline and is not read there.
    const pipelineIdBySurface: Partial<Record<AnnotationSurface, unknown>> = {
      improve_dataset: viewPipelineId,
      smart_select: viewPipelineId,
      confusion_matrix: viewPipelineId,
      model_test: evaluationPipelineId,
      model_finetune: trainingPipelineId,
    };
    const eventIdentifierId = pipelineIdBySurface[surface];

    return {
      surface,
      eventIdentifierId: typeof eventIdentifierId === 'string' ? eventIdentifierId : undefined,
    };
  }, [uiContext, pathname, returnPath, viewPipelineId, evaluationPipelineId, trainingPipelineId]);
}
